// FE-SCRM-049 — URL codec for the Prospect list filters + sort,
// plus pure client-side filter/sort helpers.
//
// The BE list endpoint (BE-SCRM-003) returns all rows for the
// tenant in one go (acceptance ≤ 1000 prospects); pagination,
// search, multi-select filters, and column sort all run
// client-side over that snapshot so the URL stays the canonical
// source of truth for deep links.

import {
  DEFAULT_SORT,
  EMPTY_FILTERS,
  VALID_FORM5500_STATUS,
  VALID_LIVES_BANDS,
  VALID_SOURCES,
  type Form5500FilterStatus,
  type LivesBand,
  type Prospect,
  type ProspectFilters,
  type ProspectSource,
  type SortKey,
} from "./types";


const URL_KEYS = {
  source: "source",
  lives_band: "lives_band",
  renewal_min: "renewal_min",
  renewal_max: "renewal_max",
  fit_min: "fit_min",
  fit_max: "fit_max",
  form5500_status: "form5500_status",
  sort: "sort",
} as const;


const VALID_SORT_KEYS: readonly SortKey[] = [
  "name", "-name",
  "renewal", "-renewal",
  "fit_score", "-fit_score",
  "created_at", "-created_at",
] as const;


function readCsv<T extends string>(
  raw: string | null, valid: readonly T[],
): T[] {
  if (!raw) return [];
  const tokens = raw.split(",").map((s) => s.trim()).filter(Boolean);
  return tokens.filter((t): t is T =>
    (valid as readonly string[]).includes(t),
  );
}


function readInt(
  raw: string | null, min: number, max: number,
): number | null {
  if (!raw) return null;
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n) || n < min || n > max) return null;
  return n;
}


function readEnum<T extends string>(
  raw: string | null, valid: readonly T[],
): T | null {
  if (!raw) return null;
  return (valid as readonly string[]).includes(raw) ? (raw as T) : null;
}


export function readProspectFiltersFromUrl(
  params: URLSearchParams,
): ProspectFilters {
  return {
    q: (params.get("q") ?? "").trim(),
    sources: readCsv<ProspectSource>(
      params.get(URL_KEYS.source), VALID_SOURCES,
    ),
    lives_bands: readCsv<LivesBand>(
      params.get(URL_KEYS.lives_band), VALID_LIVES_BANDS,
    ),
    renewal_month_min: readInt(params.get(URL_KEYS.renewal_min), 1, 12),
    renewal_month_max: readInt(params.get(URL_KEYS.renewal_max), 1, 12),
    fit_score_min: readInt(params.get(URL_KEYS.fit_min), 0, 100),
    fit_score_max: readInt(params.get(URL_KEYS.fit_max), 0, 100),
    form5500_status: readEnum<Form5500FilterStatus>(
      params.get(URL_KEYS.form5500_status), VALID_FORM5500_STATUS,
    ),
  };
}


export function writeProspectFiltersToParams(
  filters: ProspectFilters, base: URLSearchParams,
): URLSearchParams {
  const out = new URLSearchParams(base);
  // CrmSearchInput owns ?q=; this writer leaves it alone.
  for (const k of Object.values(URL_KEYS)) {
    if (k === URL_KEYS.sort) continue;
    out.delete(k);
  }
  if (filters.sources.length) {
    out.set(URL_KEYS.source, filters.sources.join(","));
  }
  if (filters.lives_bands.length) {
    out.set(URL_KEYS.lives_band, filters.lives_bands.join(","));
  }
  if (filters.renewal_month_min !== null) {
    out.set(URL_KEYS.renewal_min, String(filters.renewal_month_min));
  }
  if (filters.renewal_month_max !== null) {
    out.set(URL_KEYS.renewal_max, String(filters.renewal_month_max));
  }
  if (filters.fit_score_min !== null) {
    out.set(URL_KEYS.fit_min, String(filters.fit_score_min));
  }
  if (filters.fit_score_max !== null) {
    out.set(URL_KEYS.fit_max, String(filters.fit_score_max));
  }
  if (filters.form5500_status !== null) {
    out.set(URL_KEYS.form5500_status, filters.form5500_status);
  }
  return out;
}


export function readSortFromUrl(params: URLSearchParams): SortKey {
  const raw = params.get(URL_KEYS.sort);
  if (raw && (VALID_SORT_KEYS as readonly string[]).includes(raw)) {
    return raw as SortKey;
  }
  return DEFAULT_SORT;
}


export function writeSortToParams(
  sort: SortKey, base: URLSearchParams,
): URLSearchParams {
  const out = new URLSearchParams(base);
  out.delete(URL_KEYS.sort);
  if (sort !== DEFAULT_SORT) out.set(URL_KEYS.sort, sort);
  return out;
}


// ── Pure client-side filter / sort ────────────────────────────────


export function filterProspectRows(
  rows: Prospect[], filters: ProspectFilters,
): Prospect[] {
  const q = filters.q.trim().toLowerCase();
  return rows.filter((row) => {
    if (q) {
      const hay = [
        row.name, row.ein ?? "", row.current_broker_firm ?? "",
      ].join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (
      filters.sources.length
      && !filters.sources.includes(row.source)
    ) {
      return false;
    }
    if (filters.lives_bands.length) {
      if (!row.lives_band) return false;
      if (!filters.lives_bands.includes(row.lives_band)) return false;
    }
    if (filters.renewal_month_min !== null
        && row.renewal_month < filters.renewal_month_min) {
      return false;
    }
    if (filters.renewal_month_max !== null
        && row.renewal_month > filters.renewal_month_max) {
      return false;
    }
    if (filters.fit_score_min !== null) {
      if (row.fit_score === null) return false;
      if (row.fit_score < filters.fit_score_min) return false;
    }
    if (filters.fit_score_max !== null) {
      if (row.fit_score === null) return false;
      if (row.fit_score > filters.fit_score_max) return false;
    }
    return true;
  });
}


// Comparator pairs: each returns a stable ascending integer (positive
// when a > b, negative when a < b, zero on tie). ``sortProspectRows``
// flips the sign when the sort key is prefixed with ``-``. Nulls sort
// LAST for fit_score regardless of direction so an unscored prospect
// never accidentally tops the list.
type ProspectComparator = (a: Prospect, b: Prospect) => number;

const COMPARATORS: Record<
  "name" | "renewal" | "fit_score" | "created_at",
  ProspectComparator
> = {
  name: (a, b) => a.name.localeCompare(b.name),
  renewal: (a, b) =>
    a.renewal_year !== b.renewal_year
      ? a.renewal_year - b.renewal_year
      : a.renewal_month - b.renewal_month,
  fit_score: (a, b) => {
    if (a.fit_score === null && b.fit_score === null) return 0;
    if (a.fit_score === null) return 1;
    if (b.fit_score === null) return -1;
    return a.fit_score - b.fit_score;
  },
  created_at: (a, b) => {
    const av = a.created_at ?? "";
    const bv = b.created_at ?? "";
    if (av === bv) return 0;
    if (av === "") return 1;
    if (bv === "") return -1;
    return av.localeCompare(bv);
  },
};


export function sortProspectRows(
  rows: Prospect[], sort: SortKey,
): Prospect[] {
  const desc = sort.startsWith("-");
  const key = (
    desc ? sort.slice(1) : sort
  ) as keyof typeof COMPARATORS;
  const cmp = COMPARATORS[key];
  const out = rows.slice().sort(cmp);
  if (desc) {
    // For fit_score, null rows stay at the end on both directions —
    // partition them out and reverse only the scored half so a -fit_score
    // sort doesn't surface a null fit_score before "100".
    if (key === "fit_score") {
      const nonNull = out.filter((r) => r.fit_score !== null);
      const nulls = out.filter((r) => r.fit_score === null);
      nonNull.reverse();
      return nonNull.concat(nulls);
    }
    out.reverse();
  }
  return out;
}


export { EMPTY_FILTERS };
